/*
 * Rich-text editor: what is and isn't supported.
 *
 * Toolbar
 *   Supported:
 *     - Paragraph style: Normal, Heading 1-6
 *     - Alignment: left, center, right, justify
 *     - Text color and highlight (background color)
 *     - Bold, italic, underline, strikethrough
 *     - Bulleted and numbered lists
 *   Not supported:
 *     - Adding links: no toolbar button, no Ctrl/Cmd+K dialog, and typed or
 *       pasted URLs stay plain text (typing "https://odu.edu" does not make a
 *       link). Clicking an existing link offers only Open and Unlink.
 *     - Images, video, tables, quotes, code blocks, horizontal rules
 *     - Font family, font size
 *
 * Pasted and dropped content (cleaned by sanitizePastedHtml)
 *   Kept:
 *     - Paragraphs, line breaks, Heading 1-6, lists (including nested)
 *     - Bold, italic, underline, strikethrough, including when the source
 *       sets them as styles (Google Docs, Word):
 *         <span style="font-weight:700">Bold</span>  ->  <strong>Bold</strong>
 *     - Text color, highlight and alignment; a transparent highlight is
 *       dropped:
 *         <span style="color:#ff0000;background-color:transparent">Red</span>
 *           ->  <span style="color: rgb(255, 0, 0);">Red</span>
 *     - Links copied from outside with an https:// address:
 *         <a href="https://odu.edu" target="_blank" class="x">ODU</a>
 *           ->  <a href="https://odu.edu">ODU</a>
 *   Converted to paragraphs, each on its own line:
 *     - Quotes, code blocks and divs; line breaks inside code are kept:
 *         <blockquote>Quoted</blockquote>  ->  <p>Quoted</p>
 *         <pre>line 1\nline 2</pre>  ->  <p>line 1<br>line 2</p>
 *     - Table rows, one paragraph per row with cells separated by " | ":
 *         <tr><td>Cell 1</td><td>Cell 2</td></tr>  ->  <p>Cell 1 | Cell 2</p>
 *     - A block wrapping other blocks is removed and its contents kept:
 *         <blockquote><p>A</p><p>B</p></blockquote>  ->  <p>A</p><p>B</p>
 *   Removed, with the text kept:
 *     - Links with any address other than https:// (http:, mailto:, tel:,
 *       relative paths, javascript:, ...):
 *         <a href="javascript:alert(1)">Click</a>  ->  Click
 *         <a href="mailto:someone@odu.edu">email</a>  ->  email
 *     - Forms (inputs are removed):
 *         <form>Name: <input></form>  ->  Name:
 *     - Other tags the toolbar can't produce, such as <code>:
 *         <p>Run <code>npm test</code></p>  ->  <p>Run npm test</p>
 *     - Fonts, sizes, classes, ids, data-* and aria-* attributes, and all
 *       other styles:
 *         <span style="font-family:Arial;font-size:18pt">Big</span>  ->  Big
 *   Removed entirely:
 *     - Scripts, style blocks, event handlers (onclick, onerror, ...),
 *       iframes, embeds, form inputs, images and horizontal rules:
 *         <p onclick="steal()">Hi<script>steal()</script></p>  ->  <p>Hi</p>
 *         <p>See <img src="https://odu.edu/logo.png"> this</p>  ->  <p>See  this</p>
 *         <p>Before</p><hr><p>After</p>  ->  <p>Before</p><p>After</p>
 *   Plain text keeps one line per line, and URLs in it stay plain text. Plain
 *   text that looks like HTML is treated as HTML and cleaned by the same rules:
 *     Text before <script>alert(1)</script> text after <b>bold?</b>
 *       ->  "Text before  text after" plus bold "bold?", with no alert
 *
 * Existing content
 *   Summary HTML is cleaned by sanitizeSummaryHtml (common/helpers) before it
 *   is loaded into the editor and again before it is saved; the backend
 *   cleans it a third time on save. Only text tags (SANITIZE_CONFIG), https://
 *   links, style attributes, and the toolbar's styles (SANITIZE_ALLOWED_STYLES)
 *   are kept. Text tags the toolbar can't produce, such as tables, quotes and
 *   code blocks, stay as they are and are only changed where the user edits
 *   them. A table's cell text can be edited, but its cells can't be merged,
 *   split, resized, aligned or added and removed: the cell popup and column
 *   drag handles are turned off (see disablePlugins below).
 *
 * Tests: sanitizePastedHtml.test.js (paste) and
 * common/helpers/sanitizeHtml.test.js (summary HTML).
 */
import JoditEditor, { Jodit } from 'jodit-react';
import sanitizePastedHtml from './sanitizePastedHtml';

// Module-level so the reference stays stable; the rich-text editor is
// rebuilt whenever its config object changes.
const EDITOR_CONFIG = {
  buttons: [
    'paragraph',
    'align',
    'brush',
    '|',
    'bold',
    'italic',
    'underline',
    'strikethrough',
    '|',
    'ul',
    'ol',
  ],
  // atom() makes these lists replace the editor's defaults instead of being
  // merged into them.
  controls: {
    paragraph: {
      list: Jodit.atom({
        p: 'Normal',
        h1: 'Heading 1',
        h2: 'Heading 2',
        h3: 'Heading 3',
        h4: 'Heading 4',
        h5: 'Heading 5',
        h6: 'Heading 6',
      }),
    },
  },
  // The link plugin also owns the Ctrl/Cmd+K dialog and auto-linking of
  // pasted URLs. Existing links keep only Open and Unlink in their popup,
  // picked from the editor's default link popup: Open has no standalone
  // control, so naming it ('eye') alone shows the icon with no action.
  // select-cells owns the table cell popup (merge, split, vertical align,
  // add/delete rows and columns) and resize-cells the column drag handles.
  // Both write colspan, rowspan, width or vertical-align, which the save
  // cleaning removes (SANITIZE_CONFIG), so an existing table's text stays
  // editable but its structure can't be changed.
  disablePlugins: ['link', 'select-cells', 'resize-cells'],
  popup: {
    a: Jodit.atom(
      Jodit.defaultOptions.popup.a.filter((button) =>
        ['eye', 'unlink'].includes(button.name ?? button),
      ),
    ),
  },
  toolbarAdaptive: false,
  statusbar: false,
  hidePoweredByJodit: true,
  showCharsCounter: false,
  showWordsCounter: false,
  showXPathInStatusbar: false,
  askBeforePasteHTML: false,
  askBeforePasteFromWord: false,
  defaultActionOnPaste: 'insert_as_html',
  minHeight: 300,
  events: {
    beforePasteInsert: sanitizePastedHtml,
  },
};

/**
 * Rich-text HTML editor with a fixed toolbar. See the file header for the
 * supported toolbar actions and paste rules.
 * @param {String} value The HTML content
 * @param {(html: String) => void} onChange Called with the updated HTML
 */
export const FormRichTextEditor = ({ value, onChange }) => (
  <JoditEditor
    value={value}
    onChange={onChange}
    config={EDITOR_CONFIG}
    className="mss__richTextEditor"
  />
);
